<script lang="ts" context="module">
    export type Choice<T> = {
        value: T;
        key: string;
        label: string;
    };

    export const yesOrNoChoices: readonly Choice<boolean>[] = [
        {
            value: true,
            key: 'true',
            label: 'Oui',
        },
        {
            value: false,
            key: 'false',
            label: 'Non',
        },
    ] as const;
</script>

<script lang="ts" generics="TValues, TValue">
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import MeltCheckbox from '$components/Form/MeltCheckbox.svelte';
    import { cx } from '$lib/utils/cx.js';
    import { keep } from '$lib/utils/filterNotNull';
    import uniqWith from 'lodash/uniqWith';
    import keyBy from 'lodash/keyBy';
    import mapValues from 'lodash/mapValues';
    import findIndex from 'lodash/findIndex';

    export let form: Form<TValues, string>;
    export let field: Field<TValues, TValue[]>;

    export let choices: readonly Choice<TValue>[];
    export let areEqual: (a: TValue, b: TValue) => boolean = (a, b) => a === b;

    let className: string = '';
    export { className as class };

    export let defaultClass = 'grid grid-cols-2 gap-[16px]';

    export let defaultLabelClass = cx(
        'flex items-center',
        'px-[10px] m-0',
        'border hover:border-blue-400',
        'rounded',
        'cursor-pointer',
        //'bg-white border-gray-200',
        //'data-[checked]:bg-blue-50 data-[checked]:border-blue-200',
    );
    export let labelClass = '';

    export let defaultTextClass =
        'w-full py-4 ml-3 mb-0 text-14 text-gray-800 font-normal data-[checked]:font-bold';
    export let textClass = '';

    export let id: string | null = null;

    function onCheckboxInput(key: string, checked: boolean | 'indeterminate') {
        const choice = choices.find((choice) => choice.key === key);

        if (choice != null) {
            form.dispatch((state) => {
                let values = field.getValue(state.values);

                if (checked === true) {
                    values = uniqWith([...values, choice.value], areEqual);
                } else {
                    values = keep(values, (v) => {
                        return !areEqual(v, choice.value);
                    });
                }

                return form.actions().changeValue(values, field)(state);
            });
        }
    }

    function onBlur() {
        form.dispatch(form.actions().blur(field));
    }

    function onFocus() {
        form.dispatch(form.actions().focus(field));
    }

    $: v = form.stores().fieldValue(field);
    $: nbSubmits = form.stores().nbSubmits();
    $: isAlreadyBlur = form.stores().isAlreadyBlur(field);
    $: hasFieldErrors = form.stores().hasFieldErrors(field);
    $: effectiveId = id ?? form.getFieldId(field);
    $: choicesByKey = keyBy(choices, (c) => c.key);
    $: selectedByKey = mapValues(
        choicesByKey,
        (choice) => findIndex($v, (v) => areEqual(v, choice.value)) !== -1,
    );
</script>

<div
    class={cx(defaultClass, className)}
    class:is-invalid={($nbSubmits > 0 || $isAlreadyBlur) && $hasFieldErrors}
>
    {#each choices as c (c.key)}
        <label
            class={cx(defaultLabelClass, labelClass)}
            for={`${id}.key-${c.key}`}
            data-checked={selectedByKey[c.key] ? true : undefined}
            data-not-checked={selectedByKey[c.key] ? undefined : true}
        >
            <MeltCheckbox
                name={effectiveId}
                value={selectedByKey[c.key] ?? false}
                id="{id}.key-{c.key}"
                onChange={(value) => onCheckboxInput(c.key, value)}
                on:focus={onFocus}
                on:blur={onBlur}
            />
            <span
                class={cx(defaultTextClass, textClass)}
                data-checked={selectedByKey[c.key] ? true : undefined}
                data-not-checked={selectedByKey[c.key] ? undefined : true}>{c.label}</span
            >
        </label>
    {/each}
</div>
